import { Button, Modal, Stack } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import CheckmateSelect from '../../shared/CheckmateSelect';
import { IAssociatedOrganizationModel } from '../../../interfaces/IOrganization';
import { IMultiSelectOption } from '../../../interfaces/ILookup';
import { IValidation } from '../../../interfaces/IError';
import { IconButton } from '../../../shared/buttons/IconButton';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import UIHelper from '../../../utilities/UIHelper';
import { isEmpty } from 'lodash';
import { showErrorToast } from '../../../shared/ToastUtils';
import { useCaseFileContext } from '../../../contexts/useCaseFileContext';
import { useSaveDefenseCounselsMutation } from './useSaveDefenseCounselsMutation';

export interface ICaseDefenseCounselsModalProps {
    onClose: () => void;
    onSubmit: (currentLocalCounsels: IAssociatedOrganizationModel[]) => void;
    open: boolean;
    readonly?: boolean;
    showEditButton?: boolean;
    validation: IValidation;
}

export function CaseDefenseCounselsModal(props: ICaseDefenseCounselsModalProps) {
    if (!props.open) return null;

    const { currentCase, zoneDefenseCounsels } = useCaseFileContext();

    const saveDefenseCounselsMutation = useSaveDefenseCounselsMutation();

    const [selectedLocalCounselGuids, setSelectedLocalCounselGuids] = useState<string[]>(
        currentCase.defenseCounsels?.map((x) => x.guid as string) ?? []
    );

    const [originalLocalCounselGuids, setOriginalLocalCounselGuids] = useState<string[]>(
        currentCase.defenseCounsels?.map((x) => x.guid as string) ?? []
    );

    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [localReadonly, setLocalReadonly] = useState<boolean>(props.readonly ?? false);

    const handleCancelClick = () => {
        setSelectedLocalCounselGuids(originalLocalCounselGuids);
        props.onClose();
    };

    const handleSubmitClick = () => {
        if (hasChanges) {
            saveDefenseCounselsMutation
                .mutateAsync({
                    caseGuid: currentCase.guid!,
                    defenseCounselGuids: selectedLocalCounselGuids,
                })
                .then((updatedDefenseCounsels) => {
                    setOriginalLocalCounselGuids(
                        updatedDefenseCounsels?.map((dc) => dc.guid as string) ?? []
                    );

                    props.onSubmit([
                        ...(primaryLocalCounsel ? [primaryLocalCounsel] : []),
                        ...(nationalCounsel ? [nationalCounsel] : []),
                        ...(updatedDefenseCounsels ?? []),
                    ]);

                    setLocalReadonly(true);
                })
                .catch(() => {
                    showErrorToast(
                        <span>
                            Failed to save defense counsel for&nbsp;
                            <strong>{currentCase.caseName}</strong>
                        </span>
                    );
                });
        }
    };

    const handleOtherDefenseCounselChange = (selectedCounsels: IMultiSelectOption[]) => {
        setSelectedLocalCounselGuids(selectedCounsels.map((x) => x.value as string));
    };

    useEffect(() => {
        const diff = UIHelper.difference(originalLocalCounselGuids, selectedLocalCounselGuids);
        const hasDiff = !isEmpty(diff);
        setHasChanges(hasDiff);
    }, [originalLocalCounselGuids, selectedLocalCounselGuids]);

    useEffect(() => {
        setOriginalLocalCounselGuids(
            currentCase.defenseCounsels?.map((x) => x.guid as string) ?? []
        );
    }, [currentCase.defenseCounsels]);

    const nationalCounsel = currentCase.defenseCounsels?.find((lc) => lc.priority === -1);
    const primaryLocalCounsel = currentCase.defenseCounsels?.find((lc) => lc.priority === 1);

    const localCounselOptions = zoneDefenseCounsels
        ?.filter((dc) => dc.guid !== nationalCounsel?.guid && dc.guid !== primaryLocalCounsel?.guid)
        .map((lc) => {
            return {
                guid: lc.guid,
                value: lc.guid,
                label: lc.name,
            } as IMultiSelectOption;
        });

    return (
        <Modal show={true} centered>
            <Modal.Header>
                <Modal.Title>Defense Counsel</Modal.Title>
                {props.showEditButton && localReadonly && (
                    <IconButton
                        buttonCssClass="mr-0 btn-no-bg text-gray"
                        icon="fal fa-pen"
                        onClick={() => setLocalReadonly(false)}
                        text="Edit"
                    />
                )}
            </Modal.Header>
            <Modal.Body>
                <Stack gap={2}>
                    <Stack
                        direction="horizontal"
                        style={{ alignItems: 'center' }}
                        gap={3}
                        className="padding-left"
                    >
                        <span className="bold" style={{ minWidth: 155 }}>
                            National Counsel
                        </span>
                        <span>{nationalCounsel?.name}</span>
                    </Stack>
                    <Stack
                        style={{ alignItems: 'center' }}
                        direction="horizontal"
                        gap={3}
                        className="padding-left"
                    >
                        <span className="bold" style={{ minWidth: 155 }}>
                            Primary Local Counsel
                        </span>
                        <span>{primaryLocalCounsel?.name}</span>
                    </Stack>
                    <Stack
                        style={{ alignItems: 'start' }}
                        direction="horizontal"
                        gap={3}
                        className="padding-left"
                    >
                        <span className="bold" style={{ minWidth: 155 }}>
                            Other Defense Counsel
                        </span>
                        {localReadonly ? (
                            <span>
                                {zoneDefenseCounsels
                                    .filter(
                                        (dc) =>
                                            selectedLocalCounselGuids.includes(dc.guid ?? '') &&
                                            dc.guid !== nationalCounsel?.guid &&
                                            dc.guid !== primaryLocalCounsel?.guid
                                    )
                                    ?.map((x) => (
                                        <div key={x.guid}>{x.name}</div>
                                    ))}
                            </span>
                        ) : (
                            <div style={{ flexGrow: 1 }}>
                                <CheckmateSelect
                                    onChange={handleOtherDefenseCounselChange}
                                    options={localCounselOptions}
                                    value={localCounselOptions.filter((dc) =>
                                        selectedLocalCounselGuids.includes(dc.guid ?? '')
                                    )}
                                    isMulti
                                    isDisabled={saveDefenseCounselsMutation.isLoading}
                                />
                            </div>
                        )}
                    </Stack>
                </Stack>
            </Modal.Body>
            <Modal.Footer>
                {!localReadonly ? (
                    <Stack
                        direction="horizontal"
                        style={{ justifyContent: 'space-between', width: '100%' }}
                    >
                        <div>
                            {hasChanges && (
                                <LoadingButton
                                    variant="orange"
                                    onClick={handleSubmitClick}
                                    loading={saveDefenseCounselsMutation.isLoading}
                                    text="Save"
                                    loadingText="Saving..."
                                />
                            )}
                            <Button variant="default" onClick={handleCancelClick}>
                                Cancel
                            </Button>
                        </div>
                    </Stack>
                ) : (
                    <Button variant="dark" onClick={handleCancelClick}>
                        OK
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
